/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type AppService from 'embercom/services/app-service';
import type ReportingCsvExport from 'embercom/services/reporting-csv-export';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type ApplicationInstance from '@ember/application/instance';
import { registerDestructor } from '@ember/destroyable';
import { getOwner } from '@ember/application';
import moment from 'moment-timezone';
import Range from 'embercom/models/reporting/range';
import RenderableChart from 'embercom/models/reporting/custom/renderable-chart';
import type Chart from 'embercom/models/reporting/custom/chart';
import type Report from 'embercom/models/reporting/custom/report';
import { captureException } from 'embercom/lib/sentry';

interface Signature {
  Args: {};
}

interface DateRange {
  start: number;
  end: number;
  timezone: string;
}

// Ember logical-filter shape (matches DataConfigBuilder + RenderableChart consumers).
// React side normalizes to this shape in resolveFiltersFromBridge before dispatch.
interface ReportFilters {
  type?: 'and' | 'or' | string;
  filters?: unknown[];
}

interface ChartExportPayload {
  chartId: string;
  dateRange?: DateRange;
  filters?: ReportFilters;
  reportId?: string;
}

type ExportAction = 'export-aggregated' | 'export-source';

export default class ReportingCustomReportBridgeChartExport extends Component<Signature> {
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare reportingCsvExport: ReportingCsvExport;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  constructor(owner: ApplicationInstance, args: {}) {
    super(owner, args);

    // Inline the callback parameter type to match the bridge-chart-analyze /
    // bridge-chart-edit precedent — TypeScript's contextual variance for the
    // service's generic (data: Record<string, unknown>) => void signature
    // accepts inline-typed callbacks but rejects ones annotated with a named
    // interface, so we type inline here and trust the React-side dispatcher
    // (dispatchChartExportAggregated / -Source) for the shape.
    let unsubscribeAggregated = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'reporting:chart:export-aggregated',
      (data: {
        chartId: string;
        dateRange?: DateRange;
        filters?: ReportFilters;
        reportId?: string;
      }) => {
        this.exportAggregated(data);
      },
    );

    let unsubscribeSource = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'reporting:chart:export-source',
      (data: {
        chartId: string;
        dateRange?: DateRange;
        filters?: ReportFilters;
        reportId?: string;
      }) => {
        this.exportSource(data);
      },
    );

    registerDestructor(this, () => {
      unsubscribeAggregated();
      unsubscribeSource();
    });
  }

  private lookupChart(chartId: string, action: ExportAction): Chart | null {
    let chart = this.store.peekRecord('reporting/custom/chart', chartId) as Chart | null;
    if (!chart) {
      captureException(new Error(`BridgeChartExport: chart ${chartId} not found in store`), {
        tags: {
          feature: 'custom_reports',
          component: 'BridgeChartExport',
          responsible_team: 'team-reporting',
        },
        extra: { chartId, action },
        fingerprint: ['bridge-chart-export-missing-chart'],
      });
    }
    return chart;
  }

  private lookupReport(reportId: string | undefined, action: ExportAction): Report | null {
    if (!reportId) {
      return null;
    }
    let report = this.store.peekRecord('reporting/custom/report', reportId) as Report | null;
    if (!report) {
      // Soft-fail: report record may not be in the store (e.g., template view).
      // Falling back to null mirrors the legacy `exportChartData(chart, null)`
      // behaviour and the backend tolerates a missing report payload.
      captureException(new Error(`BridgeChartExport: report ${reportId} not found in store`), {
        tags: {
          feature: 'custom_reports',
          component: 'BridgeChartExport',
          responsible_team: 'team-reporting',
        },
        extra: { reportId, action },
        fingerprint: ['bridge-chart-export-missing-report'],
      });
    }
    return report;
  }

  private exportSource(payload: ChartExportPayload) {
    let chart = this.lookupChart(payload.chartId, 'export-source');
    if (!chart) {
      return;
    }
    // Pass the active report so the backend can apply report-level filters,
    // matching the legacy chart-action-buttons.exportSourceDataAsCsv behaviour
    // (which calls exportChartData(chart, this.args.report)). Without this,
    // /ember/reporting/conversations/export_csv runs against an unfiltered chart
    // and the CSV disagrees with what the user sees on screen.
    let report = this.lookupReport(payload.reportId, 'export-source');
    this.reportingCsvExport.exportChartData(chart, report);
  }

  private exportAggregated(payload: ChartExportPayload) {
    let chart = this.lookupChart(payload.chartId, 'export-aggregated');
    if (!chart) {
      return;
    }
    let timezone = payload.dateRange?.timezone ?? this.appService?.app?.timezone ?? 'UTC';
    // RenderableChart.selectDateRange honors reportState.dateRange when present;
    // DataConfigBuilder honors reportState.filters via buildFiltersForDataConfig.
    // Carry both through so the aggregated CSV reflects the displayed report
    // state (date range + report-level filters), not the chart's saved state.
    // payload.filters arrives in Ember logical-filter shape ({ type, filters })
    // because resolveFiltersFromBridge on the React side normalizes to that
    // shape before dispatch — including a fallback to report.filters when
    // reportState hasn't arrived yet, so this matches what the displayed
    // chart was fetched with.
    let reportState: {
      filters: ReportFilters;
      timezone: string;
      dateRange?: Range;
    } = {
      filters: payload.filters ?? { type: 'and', filters: [] },
      timezone,
    };
    if (payload.dateRange) {
      reportState.dateRange = Range.createFromParams(
        moment(payload.dateRange.start).tz(payload.dateRange.timezone).format(),
        moment(payload.dateRange.end).tz(payload.dateRange.timezone).format(),
        payload.dateRange.timezone,
      );
    }
    let report = this.lookupReport(payload.reportId, 'export-aggregated');
    let renderableChart = new RenderableChart(chart, reportState, getOwner(this));
    this.reportingCsvExport.exportChartDataPoints(renderableChart, chart, report);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::Report::BridgeChartExport': typeof ReportingCustomReportBridgeChartExport;
    'reporting/custom/report/bridge-chart-export': typeof ReportingCustomReportBridgeChartExport;
  }
}
